import { useState, type ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Banner,
  Button,
  Chip,
  Field,
  Modal,
  MoneyInput,
  PresetAmounts,
  Segmented,
  useToast,
} from '@w7/ui'
import { cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { ConsoleMfaControl } from '@/features/security/ConsoleMfaControl'
import { isConsoleMfaAgentLevel } from '@/features/security/consoleMfaCopy'
import {
  ROLE_LABEL,
  displayToPaise,
  floor2,
  formatMoney,
  humanizeCreditTransferError,
  levelToRole,
  parseCurrencyType,
  paiseToDisplay,
  round2,
  settleFromPostedPnl,
  transferCapFor,
  type HierarchyNode,
} from './hierarchy'

function roundDown(value: number): number {
  if (!(value > 0)) return 0
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)))
  return Math.floor(value / magnitude) * magnitude
}

function presetsFor(creditLimitDisplay: number, cap: number): number[] {
  const candidates = [0.1, 0.25, 0.5, 1].map((fraction) => roundDown(creditLimitDisplay * fraction))
  return [...new Set([...candidates, roundDown(cap)])]
    .filter((amount) => amount > 0 && amount <= cap)
    .sort((a, b) => a - b)
}

function emphasise(text: string, needle: string): ReactNode {
  const at = text.indexOf(needle)
  if (at === -1) return text
  return (
    <>
      {text.slice(0, at)}
      <strong>{needle}</strong>
      {text.slice(at + needle.length)}
    </>
  )
}

/**
 * Agent-UI / Backend-UI TransferDialog parity for CM:
 * amount, max, settle-to-zero — never fire-and-forget settle.
 */
export function TransferDialog({
  self,
  account,
  initialDirection,
  initialAmount,
  onClose,
}: {
  self: HierarchyNode
  account: HierarchyNode
  initialDirection: 'give' | 'take'
  /** Click-to-zero prefill (display units). */
  initialAmount?: number
  onClose: () => void
}) {
  const zeroing = settleFromPostedPnl(account.postedPnl, account.currencyType)
  // Settlement lock: outstanding Take → only Take; outstanding Give → only Give.
  const [direction, setDirection] = useState<'give' | 'take'>(
    () => zeroing?.direction ?? initialDirection,
  )
  const [raw, setRaw] = useState(initialAmount ? String(initialAmount) : '')
  const [prefilled, setPrefilled] = useState(initialAmount !== undefined)
  const [submitted, setSubmitted] = useState(false)
  const [idempotencyKey] = useState(() => crypto.randomUUID())

  const toast = useToast()
  const qc = useQueryClient()

  const childCcy = parseCurrencyType(account.currencyType)
  const selfCcy = parseCurrencyType(self.currencyType)
  const fmtChild = (n: number) => formatMoney(n, childCcy)
  const fmtSelf = (n: number) => formatMoney(n, selfCcy)

  const selfLive = paiseToDisplay(self.liveBalance ?? self.balance, selfCcy)
  const theirLive = paiseToDisplay(account.liveBalance ?? account.balance, childCcy)
  const creditLimitDisplay = paiseToDisplay(account.creditLimit, childCcy)

  const activeDirection = zeroing?.direction ?? direction
  const giving = activeDirection === 'give'
  const cleaned = raw.replace(/[^\d.]/g, '')
  const parsed = cleaned === '' ? 0 : Number(cleaned)
  const amount = Number.isFinite(parsed) ? round2(parsed) : 0

  const capInfo = transferCapFor(account, activeDirection, selfLive)
  const cap = capInfo.capDisplay
  const settlementOutstanding = zeroing != null && zeroing.direction === activeDirection

  const directionItems =
    zeroing?.direction === 'take'
      ? ([{ id: 'take' as const, label: 'Take from them' }] as const)
      : zeroing?.direction === 'give'
        ? ([{ id: 'give' as const, label: 'Give to them' }] as const)
        : ([
            { id: 'give' as const, label: 'Give to them' },
            { id: 'take' as const, label: 'Take from them' },
          ] as const)

  const problem = !(amount > 0)
    ? 'Enter an amount greater than zero.'
    : amount > cap
      ? settlementOutstanding
        ? `Amount should be less than or equal to the ${giving ? 'Give' : 'Take'} amount (${fmtChild(cap)}).`
        : giving
          ? `You have ${fmtSelf(selfLive)} available.`
          : `${account.username || account.name} holds ${fmtChild(theirLive)}. You cannot take more than they hold.`
      : null

  const showProblem = problem !== null && (submitted || amount > 0)
  const overCap = amount > 0 && amount > cap && cap > 0

  const transfer = useMutation({
    mutationFn: () =>
      cmApi.hierarchyCreditTransfer(
        authHeaders(),
        {
          id: account.id,
          direction: activeDirection,
          amount: displayToPaise(amount, childCcy),
        },
        idempotencyKey,
      ),
    onSuccess: async () => {
      toast.success(
        `${fmtChild(amount)} ${giving ? 'given to' : 'taken from'} ${account.username || account.name}`,
      )
      await qc.refetchQueries({ queryKey: ['cm', 'hierarchy'] })
      onClose()
    },
    onError: (err: Error) => {
      toast.error(humanizeCreditTransferError(err.message, account.currencyType))
    },
  })

  const setAmount = (next: number, fromPreset: boolean) => {
    setRaw(String(next))
    setPrefilled(fromPreset)
    if (transfer.isError) transfer.reset()
  }

  const switchDirection = (next: 'give' | 'take') => {
    if (zeroing && next !== zeroing.direction) return
    setDirection(next)
    setSubmitted(false)
    transfer.reset()
    if (prefilled) {
      setRaw('')
      setPrefilled(false)
    }
  }

  const submit = () => {
    setSubmitted(true)
    if (problem) return
    transfer.mutate()
  }

  const outstandingLabel =
    zeroing == null
      ? ''
      : zeroing.direction === 'take'
        ? ` · ${fmtChild(zeroing.amountDisplay)} outstanding Take`
        : ` · ${fmtChild(zeroing.amountDisplay)} outstanding Give`

  return (
    <Modal
      open
      onClose={onClose}
      title={
        giving
          ? `Give credit to ${account.username || account.name}`
          : `Take credit from ${account.username || account.name}`
      }
      subtitle={`${ROLE_LABEL[levelToRole(account.level)]} · ${account.name}${outstandingLabel}`}
      footer={
        <>
          <span className="w7-spacer" />
          <Button variant="secondary" onClick={onClose} disabled={transfer.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={transfer.isPending}
            disabled={problem !== null}
            onClick={submit}
          >
            {amount > 0
              ? `${giving ? 'Give' : 'Take'} ${fmtChild(amount)}`
              : giving
                ? 'Give credit'
                : 'Take credit'}
          </Button>
        </>
      }
    >
      <div className="cm-transfer-dialog">
        <Segmented
          label="Direction of the movement"
          value={activeDirection}
          onChange={switchDirection}
          items={[...directionItems]}
        />

        {isConsoleMfaAgentLevel(account.level) ? (
          <ConsoleMfaControl
            agentId={account.id}
            username={account.username}
            level={account.level}
            compact
          />
        ) : null}

        <Field
          label="Amount"
          required
          aside={
            cap > 0 ? (
              <Chip size="sm" tone="muted">
                Max {fmtChild(Math.floor(cap))}
              </Chip>
            ) : null
          }
          hint={emphasise(capInfo.because, fmtChild(cap))}
          error={showProblem ? problem : null}
        >
          {({ id, describedBy }) => (
            <MoneyInput
              id={id}
              aria-describedby={describedBy}
              invalid={showProblem}
              value={raw}
              autoComplete="off"
              onChange={(event) => {
                setRaw(event.target.value)
                setPrefilled(false)
                if (transfer.isError) transfer.reset()
              }}
            />
          )}
        </Field>

        {overCap ? (
          <button type="button" className="cm-transfer-chip" onClick={() => setAmount(cap, true)}>
            Settle what you can — {fmtChild(cap)}
          </button>
        ) : null}

        {zeroing && zeroing.direction === activeDirection && cap <= 0 ? (
          <Banner tone="warn" title="This cannot be settled right now">
            {capInfo.because} {fmtChild(zeroing.amountDisplay)} stays outstanding.
          </Banner>
        ) : null}

        <div className="cm-transfer-presets">
          {zeroing && zeroing.direction === activeDirection && cap > 0 ? (
            <button
              type="button"
              className="cm-transfer-chip is-settle"
              onClick={() => setAmount(floor2(zeroing.amountDisplay), true)}
            >
              Settle to zero {fmtChild(floor2(zeroing.amountDisplay))}
            </button>
          ) : null}
          <PresetAmounts
            presets={presetsFor(creditLimitDisplay, cap)}
            format={(preset) => fmtChild(preset)}
            onPick={(preset) => setAmount(preset, true)}
          />
        </div>

        {transfer.isError ? (
          <Banner tone="error" title="The credit did not move">
            {humanizeCreditTransferError(
              (transfer.error as Error)?.message ?? '',
              account.currencyType,
            )}{' '}
            Fix the amount and try again, or close — nothing has moved on this attempt.
          </Banner>
        ) : null}
      </div>
    </Modal>
  )
}
